import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { normBlocks } from "@/data/mockData";

const ClinicContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "veloclin_token";
const AVAIL_KEY = "veloclin_availability";
const SEC_KEY = "veloclin_secretaries";
const DUR_KEY = "veloclin_durations";
const HP_KEY = "veloclin_healthplans";

const save = (key, val) => {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (e) {
    // ignore
  }
};
const loadKey = (key, fallback) => {
  try {
    const r = localStorage.getItem(key);
    if (r) return JSON.parse(r);
  } catch (e) {
    // ignore
  }
  return fallback;
};

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t
    ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
};
const hasToken = () => Boolean(localStorage.getItem(TOKEN_KEY));

const DEFAULT_AVAILABILITY = {
  carlos: {
    seg: { manha: true, tarde: true },
    ter: { manha: true, tarde: true },
    qua: { manha: true, tarde: true },
    qui: { manha: true, tarde: true },
    sex: { manha: true, tarde: false },
    sab: { manha: false, tarde: false },
  },
  mariana: {
    seg: { manha: true, tarde: false },
    ter: { manha: false, tarde: false },
    qua: { manha: true, tarde: false },
    qui: { manha: false, tarde: false },
    sex: { manha: true, tarde: false },
    sab: { manha: false, tarde: false },
  },
};

const DEFAULT_SECRETARIES = [
  { id: 1, name: "Juliana Prado", email: "secretaria@clinica.com", phone: "(11) 97777-0001" },
];

const DEFAULT_DURATIONS = { carlos: 30, mariana: 30 };

const DEFAULT_HEALTH_PLANS = [
  { id: 1, name: "Particular", valorConsulta: 350, prazoDias: 0, glosaPercent: 0 },
  { id: 2, name: "Unimed", valorConsulta: 90, prazoDias: 30, glosaPercent: 8 },
  { id: 3, name: "Bradesco Saúde", valorConsulta: 110, prazoDias: 45, glosaPercent: 6 },
  { id: 4, name: "SulAmérica", valorConsulta: 105, prazoDias: 40, glosaPercent: 7 },
  { id: 5, name: "Amil", valorConsulta: 95, prazoDias: 30, glosaPercent: 7 },
];

export const ClinicProvider = ({ children }) => {
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [availability, setAvailability] = useState(() => loadKey(AVAIL_KEY, DEFAULT_AVAILABILITY));
  const [secretaries, setSecretaries] = useState(() => loadKey(SEC_KEY, DEFAULT_SECRETARIES));
  const [durations, setDurations] = useState(() => loadKey(DUR_KEY, DEFAULT_DURATIONS));
  const [healthPlans, setHealthPlans] = useState(() => loadKey(HP_KEY, DEFAULT_HEALTH_PLANS));

  useEffect(() => { save(DUR_KEY, durations); }, [durations]);
  useEffect(() => { save(HP_KEY, healthPlans); }, [healthPlans]);
  useEffect(() => { save(AVAIL_KEY, availability); }, [availability]);
  useEffect(() => { save(SEC_KEY, secretaries); }, [secretaries]);

  const fetchAppointments = useCallback(async () => {
    if (!hasToken()) { setAppointments([]); return; }
    try {
      const r = await fetch(`${API}/api/clinic/appointments`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setAppointments(d.appointments || []); }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchPatients = useCallback(async () => {
    if (!hasToken()) { setPatients([]); return; }
    try {
      const r = await fetch(`${API}/api/clinic/patients`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setPatients(d.patients || []); }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchAppointments();
    fetchPatients();
    const onAuth = () => { fetchAppointments(); fetchPatients(); };
    window.addEventListener("veloclin-auth", onAuth);
    return () => window.removeEventListener("veloclin-auth", onAuth);
  }, [fetchAppointments, fetchPatients]);

  const addPatient = useCallback(async (p) => {
    const r = await fetch(`${API}/api/clinic/patients`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(p),
    });
    const d = await r.json();
    if (r.ok) { setPatients((prev) => [d.patient, ...prev.filter((x) => x.id !== d.patient.id)]); fetchPatients(); return d.patient; }
    return null;
  }, [fetchPatients]);

  const addAppointment = useCallback(async (apt) => {
    const r = await fetch(`${API}/api/clinic/appointments`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(apt),
    });
    const d = await r.json();
    if (r.ok) { setAppointments((prev) => [...prev, d.appointment]); return d.appointment; }
    return null;
  }, []);

  const updatePatient = useCallback(async (id, p) => {
    const r = await fetch(`${API}/api/clinic/patients/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(p),
    });
    const d = await r.json();
    if (r.ok) { setPatients((prev) => prev.map((x) => (x.id === id ? { ...x, ...d.patient } : x))); return d.patient; }
    return null;
  }, []);

  const grantReturn = useCallback(async (id, grant, days = 30) => {
    const r = await fetch(`${API}/api/clinic/patients/${id}/return-grant`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ grant, days }),
    });
    const d = await r.json();
    if (r.ok) { setPatients((prev) => prev.map((x) => (x.id === id ? d.patient : x))); return d.patient; }
    throw new Error(d.detail || "Falha ao atualizar retorno");
  }, []);

  const emitAtestado = useCallback(async (payload) => {
    const r = await fetch(`${API}/api/clinic/atestado`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || "Falha ao emitir atestado");
    return d;
  }, []);

  const listRecords = useCallback(async (patientId) => {
    if (!hasToken() || !patientId) return [];
    try {
      const r = await fetch(`${API}/api/clinic/patients/${patientId}/records`, { headers: authHeaders() });
      const d = await r.json();
      return r.ok ? (d.records || []) : [];
    } catch (e) { return []; }
  }, []);

  const addRecord = useCallback(async (patientId, text, appointmentId = "") => {
    const r = await fetch(`${API}/api/clinic/patients/${patientId}/records`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ text, appointment_id: appointmentId }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || "Falha ao salvar atendimento");
    return d.record;
  }, []);

  const downloadRecordPdf = useCallback(async (recordId, filename = "atendimento-veloclin.pdf") => {
    const t = localStorage.getItem(TOKEN_KEY);
    const r = await fetch(`${API}/api/clinic/records/${recordId}/pdf`, { headers: { Authorization: `Bearer ${t}` } });
    if (!r.ok) throw new Error("Falha ao gerar o PDF");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  }, []);

  const removePatient = useCallback(async (id) => {
    const r = await fetch(`${API}/api/clinic/patients/${id}`, {
      method: "DELETE", headers: authHeaders(),
    });
    if (r.ok) setPatients((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const advanceStatus = useCallback(async (id) => {
    const r = await fetch(`${API}/api/clinic/appointments/${id}/advance`, {
      method: "PATCH", headers: authHeaders(),
    });
    const d = await r.json();
    if (r.ok) setAppointments((prev) => prev.map((a) => (a.id === id ? d.appointment : a)));
  }, []);

  const cancelAppointment = useCallback(async (id) => {
    const r = await fetch(`${API}/api/clinic/appointments/${id}/cancel`, {
      method: "PATCH", headers: authHeaders(),
    });
    const d = await r.json();
    if (r.ok) setAppointments((prev) => prev.map((a) => (a.id === id ? d.appointment : a)));
  }, []);

  const confirmPayment = useCallback(async (id, payload) => {
    const r = await fetch(`${API}/api/clinic/appointments/${id}/payment`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({
        method: payload.method,
        received: payload.received,
        splitClinica: payload.splitClinica ?? 70,
      }),
    });
    const d = await r.json();
    if (r.ok) setAppointments((prev) => prev.map((a) => (a.id === id ? d.appointment : a)));
  }, []);

  const sendWhatsAppConfirmation = useCallback(async (a) => {
    const r = await fetch(`${API}/api/whatsapp/appointment`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        to: a.patient_phone,
        template: "confirmacao_agendamento",
        patient_name: a.patient,
        date: a.date || "",
        time: a.time || "",
        provider_name: a.doctor_name || "",
      }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || "Falha ao enviar");
    return d;
  }, []);

  const createCharge = useCallback(async (id) => {
    const r = await fetch(`${API}/api/clinic/appointments/${id}/charge`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({}),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || "Falha ao gerar cobrança");
    setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, charge_url: d.url, charge_status: "pending" } : a)));
    return d;
  }, []);

  const syncCharge = useCallback(async (id) => {
    const r = await fetch(`${API}/api/clinic/appointments/${id}/sync-charge`, {
      method: "POST", headers: authHeaders(),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || "Falha ao sincronizar pagamento");
    if (d.appointment) setAppointments((prev) => prev.map((a) => (a.id === id ? d.appointment : a)));
    return d;
  }, []);

  const resetDay = useCallback(() => { fetchAppointments(); }, [fetchAppointments]);

  const _setBlocks = (prev, doctorId, dayKey, period, blocks) => ({
    ...prev,
    [doctorId]: {
      ...(prev[doctorId] || {}),
      [dayKey]: { ...(prev[doctorId]?.[dayKey] || {}), [period]: blocks },
    },
  });

  const toggleShift = useCallback((doctorId, dayKey, period) => {
    setAvailability((prev) => {
      const cur = normBlocks(prev[doctorId]?.[dayKey]?.[period], period);
      const next = cur.length ? [] : normBlocks(true, period);
      return _setBlocks(prev, doctorId, dayKey, period, next);
    });
  }, []);

  const addBlock = useCallback((doctorId, dayKey, period) => {
    setAvailability((prev) => {
      const cur = normBlocks(prev[doctorId]?.[dayKey]?.[period], period);
      return _setBlocks(prev, doctorId, dayKey, period, [...cur, ...normBlocks(true, period)]);
    });
  }, []);

  const removeBlock = useCallback((doctorId, dayKey, period, idx) => {
    setAvailability((prev) => {
      const cur = normBlocks(prev[doctorId]?.[dayKey]?.[period], period);
      return _setBlocks(prev, doctorId, dayKey, period, cur.filter((_, i) => i !== idx));
    });
  }, []);

  const setBlockTime = useCallback((doctorId, dayKey, period, idx, field, val) => {
    setAvailability((prev) => {
      const cur = normBlocks(prev[doctorId]?.[dayKey]?.[period], period);
      const next = cur.map((b, i) => (i === idx ? { ...b, [field]: val } : b));
      return _setBlocks(prev, doctorId, dayKey, period, next);
    });
  }, []);

  const setDuration = useCallback((doctorId, min) => {
    setDurations((prev) => ({ ...prev, [doctorId]: min }));
  }, []);

  const addHealthPlan = useCallback((p) => setHealthPlans((prev) => [...prev, { id: Date.now(), ...p }]), []);
  const updateHealthPlan = useCallback((id, patch) => setHealthPlans((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p))), []);
  const removeHealthPlan = useCallback((id) => setHealthPlans((prev) => prev.filter((p) => p.id !== id)), []);

  const addSecretary = useCallback((s) => {
    const sec = { id: Date.now(), ...s };
    setSecretaries((prev) => [...prev, sec]);
    return sec;
  }, []);

  const removeSecretary = useCallback((id) => {
    setSecretaries((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Faturamento do dia derivado da agenda — alimenta o DRE do financeiro
  const todayCash = useMemo(() => {
    const paid = appointments.filter((a) => a.paid);
    let gross = 0;
    let repasseMedico = 0;
    paid.forEach((a) => {
      const received = a.received ?? 0;
      const clinica = (received * (a.splitClinica ?? 70)) / 100;
      gross += received;
      repasseMedico += received - clinica;
    });
    return {
      gross,
      repasseMedico,
      liquidoClinica: gross - repasseMedico,
      count: paid.length,
    };
  }, [appointments]);

  const value = useMemo(
    () => ({
      appointments,
      advanceStatus,
      confirmPayment,
      resetDay,
      todayCash,
      availability,
      toggleShift,
      addBlock,
      removeBlock,
      setBlockTime,
      durations,
      setDuration,
      healthPlans,
      addHealthPlan,
      updateHealthPlan,
      removeHealthPlan,
      patients,
      addPatient,
      updatePatient,
      grantReturn,
      emitAtestado,
      listRecords,
      addRecord,
      downloadRecordPdf,
      removePatient,
      addAppointment,
      cancelAppointment,
      sendWhatsAppConfirmation,
      createCharge,
      syncCharge,
      secretaries,
      addSecretary,
      removeSecretary,
    }),
    [
      appointments,
      advanceStatus,
      confirmPayment,
      resetDay,
      todayCash,
      availability,
      toggleShift,
      addBlock,
      removeBlock,
      setBlockTime,
      durations,
      setDuration,
      healthPlans,
      addHealthPlan,
      updateHealthPlan,
      removeHealthPlan,
      patients,
      addPatient,
      updatePatient,
      grantReturn,
      emitAtestado,
      listRecords,
      addRecord,
      downloadRecordPdf,
      removePatient,
      addAppointment,
      cancelAppointment,
      sendWhatsAppConfirmation,
      createCharge,
      syncCharge,
      secretaries,
      addSecretary,
      removeSecretary,
    ]
  );

  return <ClinicContext.Provider value={value}>{children}</ClinicContext.Provider>;
};

export const useClinic = () => {
  const ctx = useContext(ClinicContext);
  if (!ctx) throw new Error("useClinic deve ser usado dentro de ClinicProvider");
  return ctx;
};
